// ============================================================================
// JARVISTRAVEL - FORGOT PASSWORD FLOW
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { taskRouter } from './sessionTask';
import { clerkErrorMessage, clerkFieldError } from '../../utils/clerkError';
import { usePasswordRules, checkPassword, describePasswordRules } from './passwordRules';
import { ChevronLeft, Mail, ShieldCheck, KeyRound, Check } from 'lucide-react';
import { Button, Input, Modal } from '@/app/components/shared';
import { validateEmail } from '@/app/utils';
import { DEV_TEST_EMAIL } from '@/app/utils/devEmail';
import { CODE_LENGTH, emptyCode, RESEND_COOLDOWN_SECONDS } from './emailCode';
import { describeIncompleteStatus } from './signInStatus';
import { useSecondFactor } from './useSecondFactor';
import { SecondFactorPanel } from './SecondFactorPanel';
import { resetAwaitingCode, resetAwaitingNewPassword } from './resumeAuth';

interface ForgotPasswordScreenProps {
  onBack: () => void;
  onComplete: () => void;
  /**
   * Where to send someone whose new session carries an outstanding Clerk task,
   * which on this instance means setting up a second factor. Optional because
   * the routing belongs to the app, not to this screen (JAR-1700).
   */
  onSessionTask?: (path: string) => void;
}

// 'password-set' is terminal and now narrow: the reset succeeded, the account
// needs a second factor, and it is one this app cannot complete (a phone or email
// code). A TOTP or backup-code account no longer comes here: it finishes the
// second factor in place, on the shared panel (JAR-1537).
type ForgotPasswordStep = 'email' | 'verify' | 'reset' | 'password-set';

export function ForgotPasswordScreen({ onBack, onComplete, onSessionTask }: ForgotPasswordScreenProps) {
  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState(DEV_TEST_EMAIL);
  const [code, setCode] = useState<string[]>(emptyCode);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { isLoaded, signIn, setActive } = useSignIn();
  // The reset password is judged by Clerk, so the rule is Clerk's (JAR-1702).
  const passwordRules = usePasswordRules();
  // The second factor, shared with SignInScreen (JAR-1537). On success the
  // session is already active; what is left is the same as a reset that needed
  // no second factor, which is the success modal.
  const secondFactor = useSecondFactor({ onComplete: () => setShowSuccessModal(true) });
  // Set only when the account's second factor is one this app cannot complete.
  const [refusal, setRefusal] = useState('');

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // A reset that a reload or relaunch interrupted starts where it was: at its
  // code, which still works, or at the new password once the code was accepted,
  // instead of asking for the address and sending another (JAR-2492, JAR-2534).
  // Checked once, on the first render Clerk has loaded.
  const resumeChecked = useRef(false);
  useEffect(() => {
    if (!isLoaded || resumeChecked.current) return;
    resumeChecked.current = true;
    const resumeAt = resetAwaitingNewPassword(signIn) ? 'reset' : resetAwaitingCode(signIn) ? 'verify' : null;
    if (!resumeAt) return;
    setEmail(signIn?.identifier ?? '');
    setStep(resumeAt);
  }, [isLoaded, signIn]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      if (!isLoaded) return;
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setStep('verify');
      setCountdown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setErrors({
        email: clerkErrorMessage(
          err,
          'Couldn\'t send the code.',
          'Couldn\'t send the code. Please try again.',
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setErrors({});
    if (value && index < CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    const focusIndex = Math.min(pastedData.length, CODE_LENGTH - 1);
    codeInputRefs.current[focusIndex]?.focus();
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== CODE_LENGTH) {
      setErrors({ code: `Please enter the complete ${CODE_LENGTH}-digit code` });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      if (!isLoaded) return;
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: fullCode,
      });
      if (result.status === 'needs_new_password') {
        setStep('reset');
      } else {
        setErrors({ code: 'That code didn\'t work. Please try again.' });
      }
    } catch (err) {
      setErrors({
        code: clerkErrorMessage(err, 'Verification failed', 'That code didn\'t work. Please try again.'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    setIsLoading(true);
    try {
      if (!isLoaded) return;
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setCountdown(RESEND_COOLDOWN_SECONDS);
      setCode(emptyCode());
    } catch (err) {
      setErrors({
        code: clerkErrorMessage(err, 'Couldn\'t resend.', 'Couldn\'t resend the code. Please try again.'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const newErrors: Record<string, string> = {};
    if (!newPassword) newErrors.newPassword = 'Password is required';
    else {
      const broken = checkPassword(newPassword, passwordRules);
      if (broken) newErrors.newPassword = broken;
    }
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsLoading(true);
    setErrors({});
    try {
      if (!isLoaded) return;
      const result = await signIn.resetPassword({ password: newPassword });
      if (result.status === 'complete') {
        const task = taskRouter(onSessionTask);
        await setActive({
          session: result.createdSessionId,
          // A user with no second factor yet gets a PENDING session carrying
          // Clerk's setup-mfa task, which only its own screen finishes (JAR-1700).
          navigate: task.navigate,
        });
        // Not when a task was routed: the enrolment screen owns the page now,
        // and a success modal over it is a dialog nobody can act on.
        if (task.routed) return;
        setShowSuccessModal(true);
      } else if (result.status === 'needs_second_factor') {
        // THE PASSWORD IS SET. Clerk returns this when the account carries a
        // second factor, and it is not a failure: it is the rest of the sign-in
        // (JAR-1481). This branch once fell through and said "Couldn't reset your
        // password. Please try again.", which was false twice over.
        //
        // IT FINISHES HERE NOW (JAR-1537). Until the second-factor panel was
        // shared, this screen could not ask for the code, so it sent the user to
        // sign in with the password they had just set: two steps where one would
        // do. The panel and its one attemptSecondFactor call now live in
        // useSecondFactor, which SignInScreen uses too, so this is not a second
        // copy of anything.
        //
        // The form goes away either way (web-app#370 review). Left in place, its
        // Reset button stayed live and pressing it called resetPassword on a
        // sign-in already waiting for its second factor.
        setNewPassword('');
        setConfirmPassword('');
        const refused = secondFactor.enter(result.supportedSecondFactors, email, false);
        if (refused) {
          // A factor this app cannot complete. The password IS set, so say that
          // first, then the honest refusal, with sign-in as the way forward.
          setRefusal(refused);
          setStep('password-set');
        }
      } else {
        // Name the status rather than invent a reason, the rule SignInScreen
        // already follows. "Please try again" was advice that could not work.
        setErrors({ confirmPassword: describeIncompleteStatus(result.status) });
      }
    } catch (err) {
      // A refused password belongs under the password, not under its confirmation
      // (JAR-1702). Everything else keeps its old place.
      const passwordRefusal = clerkFieldError(err, 'password');
      if (passwordRefusal) {
        setErrors({ newPassword: passwordRefusal });
        return;
      }
      setErrors({
        confirmPassword: clerkErrorMessage(
          err,
          'Reset failed',
          'Couldn\'t reset your password. Please try again.',
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onComplete();
  };

  const stepIcon = { email: Mail, verify: ShieldCheck, reset: KeyRound, 'password-set': ShieldCheck }[step];
  const StepIcon = stepIcon;
  // The second factor and 'password-set' both come after the reset step, so the
  // bar is full for either.
  const progressIndex = step === 'password-set' || secondFactor.active ? 2 : ['email', 'verify', 'reset'].indexOf(step);

  return (
    <div className="min-h-[100dvh] bg-bg flex flex-col">
      {/* Header */}
      <div className="pt-8 pb-4 px-6">
        <div className="flex items-center">
          <button
            onClick={step === 'email' || step === 'password-set' || secondFactor.active ? onBack : () => setStep('email')}
            // Disabled while a code is verifying, like the panel's own way out: the
            // attemptSecondFactor in flight still lands, and would sign in a user who
            // chose to leave (web-app#305, and web-app#384's review for this button).
            disabled={secondFactor.isVerifying}
            aria-label="Back"
            className="p-2 -ml-2 text-muted hover:text-ink disabled:opacity-50"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>
          <h1 className="text-xl font-bold text-ink ml-2">Reset Password</h1>
        </div>
        <div className="flex gap-2 mt-6 px-4">
          {['email', 'verify', 'reset'].map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${progressIndex >= i ? 'bg-brand' : 'bg-surface-raised'}`} />
          ))}
        </div>
      </div>

      {/* The second factor replaces the form card rather than nesting in it: the
          panel is a whole card with its own heading (JAR-1537). */}
      {secondFactor.active ? (
        <SecondFactorPanel
          secondFactor={secondFactor}
          onBack={onBack}
          passwordNote=""
          statusNotice="Your new password is set."
          // Named for where it goes, as the password-set step names it. The panel's
          // default, "Use a different account", is sign-in wording (JAR-1560).
          backLabel="Go to sign in"
        />
      ) : (
      <div className="flex-1 px-6 pb-8">
        <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-accent/10 rounded-2xl flex items-center justify-center text-accent mb-4">
              <StepIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h2 className="text-2xl font-bold text-ink">
              {step === 'email' ? 'Forgot password?' : step === 'verify' ? 'Check your email' : step === 'password-set' ? 'Password updated' : 'Create new password'}
            </h2>
            <p className="text-muted mt-2">
              {step === 'email' ? "Enter your email and we'll send you a code."
                : step === 'verify' ? <>Code sent to <span className="font-medium text-ink">{email}</span></>
                : step === 'password-set' ? 'One more step to sign in.'
                : 'Enter a new password for your account.'}
            </p>
          </div>

          {step === 'email' && (
            <div className="space-y-5">
              <Input type="email" label="Email Address" placeholder="your@email.com" value={email} onChange={(e) => { setEmail(e.target.value); setErrors({}); }} error={errors.email} leftIcon={<Mail className="w-5 h-5" strokeWidth={1.75} />} />
              <Button onClick={handleSendCode} variant="primary" fullWidth size="lg" loading={isLoading}>Send Verification Code</Button>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-body mb-3 text-center">Enter code</label>
                <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { codeInputRefs.current[index] = el; }}
                      type="text" inputMode="numeric" maxLength={1} value={digit}
                      autoComplete="one-time-code" name={`otp-${index}`}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      className={`w-10 h-12 text-center text-xl font-bold bg-surface-raised text-ink border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/40 ${errors.code ? 'border-danger' : 'border-border'}`}
                    />
                  ))}
                </div>
                {errors.code && <p className="text-sm text-danger text-center mt-2">{errors.code}</p>}
              </div>
              <Button onClick={handleVerifyCode} variant="primary" fullWidth size="lg" loading={isLoading}>Verify Code</Button>
              <p className="text-sm text-muted text-center">
                Didn't receive the code?{' '}
                {countdown > 0 ? (
                  <span className="text-muted">Resend in {countdown}s</span>
                ) : (
                  <button onClick={handleResendCode} className="text-accent font-medium hover:opacity-80">Resend Code</button>
                )}
              </p>
            </div>
          )}

          {step === 'reset' && (
            <div className="space-y-5">
              <Input type={showPassword ? 'text' : 'password'} label="New Password" placeholder="Create a strong password" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setErrors((prev) => ({ ...prev, newPassword: '' })); }} error={errors.newPassword} helperText={describePasswordRules(passwordRules)} leftIcon={<KeyRound className="w-5 h-5" strokeWidth={1.75} />} />
              <Input type={showPassword ? 'text' : 'password'} label="Confirm New Password" placeholder="Confirm your new password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setErrors((prev) => ({ ...prev, confirmPassword: '' })); }} error={errors.confirmPassword} leftIcon={<KeyRound className="w-5 h-5" strokeWidth={1.75} />} />
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} className="w-4 h-4 accent-brand rounded" />
                <span className="text-sm text-body">Show passwords</span>
              </label>
              <Button onClick={handleResetPassword} variant="primary" fullWidth size="lg" loading={isLoading}>Reset Password</Button>
            </div>
          )}

          {step === 'password-set' && (
            <div className="space-y-5">
              <p role="status" className="text-sm text-body text-center">
                Your new password is set. {refusal}
              </p>
              <Button onClick={onBack} variant="primary" fullWidth size="lg">Go to sign in</Button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={handleSuccessClose} showCloseButton={false} size="sm">
        <div className="text-center py-4">
          <div className="w-20 h-20 mx-auto bg-success/10 rounded-full flex items-center justify-center text-success mb-4">
            <Check className="w-10 h-10" strokeWidth={2} />
          </div>
          <h3 className="text-2xl font-bold text-ink mb-2">Password reset</h3>
          <p className="text-muted mb-6">Your password has been successfully reset.</p>
          <Button onClick={handleSuccessClose} variant="primary" fullWidth size="lg">Back to Sign In</Button>
        </div>
      </Modal>
    </div>
  );
}

export default ForgotPasswordScreen;
